/**
 * `/lot/$challengeId` — lot du Live School à CHOISIR (migration 0331) : l'élève
 * du podium possédait déjà la SKILLCARD mise en jeu, il choisit celle qu'il
 * veut (cartes du programme du live en tête, cartes déjà possédées exclues).
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, Gift } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import {
  livePrizeChoiceErrorMessage,
  prizePlaceLabel,
  useChooseLivePrize,
  useLivePrizeChoiceCards,
  useMyLivePrizeChoices,
} from '@/lib/live-prize-choice';
import {
  SkillcardPickerField,
  toSkillcardPickerItem,
} from '@/features/skillcards/skillcard-picker';

export function LivePrizeChoicePage({ challengeId }: { challengeId: string }) {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const choices = useMyLivePrizeChoices(userId);
  const cards = useLivePrizeChoiceCards(challengeId);
  const choose = useChooseLivePrize(userId);
  const [picked, setPicked] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);

  const award = (choices.data ?? []).find((c) => c.challenge_id === challengeId) ?? null;
  const list = cards.data ?? [];
  const items = list.map(toSkillcardPickerItem);
  const programmeCount = list.filter((c) => c.in_programme).length;

  return (
    <div className="mx-auto max-w-lg space-y-5 px-4 py-6 pb-24 sm:py-8">
      <Link
        to="/student/news"
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Live School
      </Link>

      <div className="space-y-1">
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-2xl font-extrabold">
          <Gift className="text-accent-500 h-6 w-6 shrink-0" aria-hidden /> Choisis ta SKILLCARD
        </h1>
        {award && (
          <p className="text-ink-600 text-sm">
            {prizePlaceLabel(award.rank)} au {award.title ?? 'Live School'} ! Tu possèdes déjà la
            SKILLCARD mise en jeu
            {award.prize_title ? ` (« ${award.prize_title.replace(/\$/g, '')} »)` : ''} : choisis
            celle que tu veux à la place.
          </p>
        )}
      </div>

      {chosen ? (
        <div className="space-y-3 rounded-2xl bg-green-50 p-4 text-green-800" role="status">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden /> C’est fait : la SKILLCARD est
            dans ton deck.
          </p>
          <Link
            to="/card/$cardId"
            params={{ cardId: chosen }}
            className="bg-primary-500 inline-flex min-h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold text-white active:scale-[0.98]"
          >
            Voir ma SKILLCARD
          </Link>
        </div>
      ) : choices.isLoading || cards.isLoading ? (
        <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
      ) : !award || cards.data === null ? (
        <p className="text-ink-500 text-sm">
          Aucun lot à choisir pour ce live (il a peut-être déjà été choisi).
        </p>
      ) : list.length === 0 ? (
        <p className="text-ink-500 text-sm">Tu possèdes déjà toutes les SKILLCARDS publiées !</p>
      ) : (
        <div className="space-y-4">
          {programmeCount > 0 && (
            <p className="text-ink-500 text-xs">
              Les {programmeCount} SKILLCARDS du programme du live sont proposées en premier.
            </p>
          )}
          <SkillcardPickerField
            label="Ta SKILLCARD"
            title="Choisir ma SKILLCARD"
            items={items}
            value={picked}
            onChange={setPicked}
            disabled={choose.isPending}
          />
          <Button
            fullWidth
            size="lg"
            disabled={!picked || choose.isPending}
            onClick={() =>
              picked &&
              choose.mutate(
                { challengeId, cardId: picked },
                { onSuccess: (cardId) => setChosen(cardId) },
              )
            }
          >
            {choose.isPending ? 'Enregistrement…' : 'Je prends cette SKILLCARD'}
          </Button>
          {choose.error && (
            <p role="alert" className="text-error-600 text-sm">
              {livePrizeChoiceErrorMessage(choose.error.message)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
